import * as React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import { NewsArticleFieldsFragment } from '../../../@types/generated';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import { formatLink } from '@/utils/utils';
import clsx from 'clsx';
import { Heading, Paragraph, Subheading } from 'components/src/typography';
import VideoPlayer from '../VideoPlayer';
import dynamic from 'next/dynamic';
const VideoJSPlayer = dynamic(() => import('@/components/VideoJS/VideoPlayer'), { ssr: false });
import UnorderedList from '../RichTextSection/UnorderedList';

type Props = {
  data: NewsArticleFieldsFragment;
};

const getEntry = (data: NewsArticleFieldsFragment, id: string) => {
  if (!data.news?.links.entries.block) {
    return;
  }

  return data.news.links.entries.block.find((x) => x?.sys.id === id);
};

const RichTextContainer = ({ data }: Props) => {
  const defaultStyle = 'col-span-full text-black-100 lg:col-span-8 lg:col-start-2 whitespace-break-spaces empty:h-16';
  const content = data.news?.json as Document;
  const imageIds = content.content.reduce<string[]>((acc, x, index) => {
    if (x.nodeType === 'embedded-entry-block') {
      if (content.content[index + 1]?.nodeType === 'embedded-entry-block') {
        acc.push(content.content[index + 1].data.target.sys.id, x.data.target.sys.id);
      }
    }
    return acc;
  }, []);

  return (
    <>
      {documentToReactComponents(data.news?.json as Document, {
        renderNode: {
          [BLOCKS.HEADING_6]: (_node, children) => <Subheading classes={defaultStyle}>{children}</Subheading>,
          [BLOCKS.HEADING_5]: (_node, children) => (
            <Heading classes={defaultStyle} variant="xs">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_4]: (_node, children) => (
            <Heading classes={defaultStyle} variant="small">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_3]: (_node, children) => (
            <Heading classes={defaultStyle} variant="medium">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_2]: (_node, children) => (
            <Heading classes={defaultStyle} variant="large">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_1]: (_node, children) => (
            <Heading classes={defaultStyle} variant="xl">
              {children}
            </Heading>
          ),
          [BLOCKS.PARAGRAPH]: (_node, children) => {
            return (
              <Paragraph variant="large" classes={`${clsx('font-normal', defaultStyle)}`}>
                {children}
              </Paragraph>
            );
          },
          [BLOCKS.OL_LIST]: (_node, children) => {
            return (
              <ol className={`${clsx('ml-24 list-decimal text-lg font-normal leading-8', defaultStyle)}`}>
                {children}
              </ol>
            );
          },
          [BLOCKS.UL_LIST]: (_node, children) => {
            return (
              <UnorderedList classes={`${clsx('ml-24 text-lg font-normal leading-8', defaultStyle)}`}>
                {children}
              </UnorderedList>
            );
          },
          [BLOCKS.QUOTE]: (_node, children) => {
            return (
              <div className="col-span-9 col-start-2 block rounded-28 bg-red-100 p-16 md:p-32 lg:col-span-7 lg:col-start-1">
                <div className="[&>p]:text-white-100">{children}</div>
              </div>
            );
          },
          [INLINES.HYPERLINK]: (node, children) => {
            return (
              <a
                href={formatLink(node.data.uri)}
                className="text-lg text-red-100 hover:underline hover:decoration-red-100"
                target="_blank"
                rel="noreferrer"
              >
                {children}
              </a>
            );
          },
          [BLOCKS.HR]: (_node, children) => {
            return <div className="col-span-full h-[1px] bg-black-50">{children}</div>;
          },
          [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            const entry = getEntry(data, node.data.target.sys.id);
            if (entry?.__typename === 'ImageWithFocalPoint') {
              const isImageSideBySide = imageIds.includes(node.data.target.sys.id);
              return (
                <div
                  key={entry.sys.id}
                  className={`relative col-span-full h-[360px] lg:h-[496px] ${clsx({
                    'lg:col-span-5': isImageSideBySide,
                  })}`}
                >
                  <ImageWithFocalPoint image={entry} sectionId={entry.id} className="rounded-28" />
                </div>
              );
            } else if (entry?.__typename === 'Video') {
              return (
                <div className="col-span-full h-[360px] w-full overflow-hidden rounded-28 md:h-[496px] lg:col-span-10">
                  <VideoPlayer
                    videoId={entry.youtubeId}
                    autoPlay={entry.autoplay}
                    muted={entry.muted}
                    loop={entry.loop}
                  />
                </div>
              );
            } else if (entry?.__typename === 'VideoJs') {
              return (
                <div className="col-span-full w-full overflow-hidden rounded-28 lg:col-span-10">
                  <VideoJSPlayer
                    options={{ sources: [{ src: entry.mediaFileUrl, type: 'application/x-mpegURL' }], ...entry }}
                  />
                </div>
              );
            }
            return null;
          },
        },
      })}
    </>
  );
};
export default RichTextContainer;
